import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Lines, SectionTag, EASE } from "../site/Motion";
import { IMG } from "../../lib/images";

export function Monoq() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const carOpacity = useTransform(scrollYProgress, [0.1, 0.5], [1, 0.1]);
  const wireOpacity = useTransform(scrollYProgress, [0.2, 0.55], [0.15, 1]);
  const wireScale = useTransform(scrollYProgress, [0.2, 0.55], [1.04, 1]);

  return (
    <section id="monoq" data-testid="monoq-section" ref={ref} className="relative overflow-hidden border-t border-white/10 bg-black py-28 sm:py-40">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <SectionTag index="06" label="Struttura MONOQ™" />
        <div className="mt-10 grid items-center gap-16 lg:grid-cols-2">
          <div className="relative order-2 overflow-hidden border border-white/10 lg:order-1">
            <motion.img
              src={IMG.carBase}
              alt="SWAPA vista di profilo"
              className="w-full object-cover opacity-80"
              style={reduce ? {} : { opacity: carOpacity }}
              loading="lazy"
            />
            <motion.div
              className="absolute inset-0 flex items-center bg-[#f4f4f2]"
              style={reduce ? { opacity: 0.92 } : { opacity: wireOpacity }}
            >
              <motion.img
                src="/assets/monoq-exploded.webp"
                alt="Struttura monoscocca MONOQ esplosa: scocca, rinforzi e pannelli"
                className="h-full w-full object-cover"
                style={reduce ? {} : { scale: wireScale }}
                loading="lazy"
              />
            </motion.div>
            <p className="absolute bottom-4 left-4 z-10 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mix-blend-difference">
              MONOQ™ — Monoscocca alluminio / acciaio
            </p>
          </div>
          <div className="order-1 lg:order-2">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-swapa-yellow">MONOQ™</p>
            <h2 className="mt-4 font-display font-extrabold uppercase leading-[0.92] tracking-tight">
              <Lines lines={["Built like a car.", "Sized for the city."]} className="text-4xl text-white sm:text-6xl lg:text-7xl" />
            </h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.25, duration: 0.9, ease: EASE }}
              className="mt-8 max-w-lg text-base leading-relaxed text-zinc-400"
            >
              Sotto le dimensioni compatte, SWAPA ha una vera architettura automobilistica:
              una struttura monoscocca in alluminio e acciaio con stadi di assorbimento
              differenziati. Automotive dentro, città fuori.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35, duration: 0.9, ease: EASE }}
            >
              <Link
                to="/tecnologia#monoq"
                data-testid="monoq-discover-cta"
                className="group mt-10 inline-flex items-center gap-3 border border-white/30 px-7 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-swapa-yellow hover:text-swapa-yellow"
              >
                Scopri MONOQ™
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
